const chapter = {
  id: 'p-block-16',
  slug: 'p-block-16',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'p-Block — Group 16 (Oxygen Family)',
        hindi: 'p-खण्ड — वर्ग 16 (ऑक्सीजन परिवार)',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'general',
      title: {
        english: 'General — O, S, Se, Te, Po',
        hindi: 'सामान्य - ओ, एस, से, टे, पो',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Configuration $ns^2 np^4$. Common O.S. $-2, +2, +4, +6$ (O mostly $-2$; +ve only with F).',
        hindi: 'कॉन्फ़िगरेशन $ns^2 np^4$। सामान्य ओ.एस. $-2, +2, +4, +6$ (O अधिकतर $-2$; केवल F के साथ +ve)।',
            },
            {
              english: 'Down the group: metallic character $\\uparrow$ (O, S non-metal; Se, Te metalloid; Po metal/radioactive).',
        hindi: 'समूह में नीचे: धात्विक लक्षण $\\uparrow$ (O, S अधातु; Se, Te उपधातु; पो धातु/रेडियोधर्मी)।',
            },
            {
              english: 'O exists as $O_2$ (diatomic, paramagnetic — 2 unpaired e⁻ in MO theory) and $O_3$ (ozone). Others as $S_8$, $Se_8$ rings.',
        hindi: 'O $O_2$ (डायटोमिक, पैरामैग्नेटिक - MO सिद्धांत में 2 अयुग्मित e⁻) और $O_3$ (ओजोन) के रूप में मौजूद है। अन्य जैसे $S_8$, $Se_8$ बजते हैं।',
            },
            {
              english: 'O is unique: small, high EN (3.5), no d-orbital ⇒ max covalency 2; H₂O liquid (H-bonding); H₂S gas.',
        hindi: 'O अद्वितीय है: छोटा, उच्च EN (3.5), कोई d-ऑर्बिटल नहीं ⇒ अधिकतम सहसंयोजकता 2; H₂O तरल (H-बॉन्डिंग); H₂S गैस.',
            },
            {
              english: 'Catenation: S > O. Bond strength S–S > O–O (lone-pair repulsion in O–O).',
        hindi: 'शृंखला: एस > ओ. बंधन शक्ति एस-एस > ओ-ओ (ओ-ओ में अकेला-जोड़ी प्रतिकर्षण)।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Property', 'O', 'S', 'Se', 'Te', 'Po'],
          rows: [
            ['Atomic radius (pm)', '66', '104', '117', '137', '146'],
            ['EN (Pauling)', '3.5', '2.5', '2.4', '2.1', '2.0'],
            ['$E_a$ (kJ/mol)', '−141', '−200', '−195', '−190', '−174'],
            ['Common O.S.', '−2', '−2,+2,+4,+6', '−2,+2,+4,+6', '−2,+2,+4,+6', '+2,+4'],
          ],
        },
      ],
    },
    {
      id: 'dioxygen-ozone',
      title: {
        english: 'Dioxygen & Ozone',
        hindi: 'डाइऑक्सीजन और ओज़ोन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'O₂ prep: $2KClO_3 \\xrightarrow{MnO_2,\\Delta} 2KCl + 3O_2$; $2H_2O_2 \\xrightarrow{MnO_2} 2H_2O + O_2$.',
        hindi: 'O₂ तैयारी: $2KClO_3 \\xrightarrow{MnO_2,\\Delta} 2KCl + 3O_2$; $2H_2O_2 \\xrightarrow{MnO_2} 2H_2O + O_2$.',
            },
            {
              english: 'O₂ is paramagnetic (MO: $\\sigma^2 \\sigma^{*2} \\sigma^2 \\pi^4 \\pi^{*2}$, two unpaired electrons).',
        hindi: 'O₂ अनुचुंबकीय है (MO: $\\sigma^2 \\sigma^{*2} \\sigma^2 \\pi^4 \\pi^{*2}$, दो अयुग्मित इलेक्ट्रॉन)। **ओजोन O₃**: तैयारी - O₂ में मूक विद्युत निर्वहन: $\\sigma^2 \\sigma^{*2} \\sigma^2 \\pi^4 \\pi^{*2}$, (एंडोथर्मिक, इतना अस्थिर)।',
            },
            {
              english: '**Ozone O₃**: prep — silent electric discharge in O₂: $3O_2 \\to 2O_3$, $\\Delta H = +142$ kJ/mol (endothermic, so unstable).',
        hindi: '**ओजोन O₃**: तैयारी - O₂ में मूक विद्युत निर्वहन: $3O_2 \\to 2O_3$, $\\Delta H = +142$ kJ/mol (एंडोथर्मिक, इतना अस्थिर)।',
            },
            {
              english: 'Structure: bent, $sp^2$, **bond angle 117°**, two equivalent O–O bonds (resonance, bond order 1.5).',
        hindi: 'संरचना: मुड़ा हुआ, $sp^2$, **बॉन्ड कोण 117°**, दो समतुल्य O–O बॉन्ड (प्रतिध्वनि, बॉन्ड क्रम 1.5)। O₃ एक **मजबूत ऑक्सीडेंट** है: $sp^2$ (मुक्ति द्वारा अनुमान I₂ का शीर्षक है)।',
            },
            {
              english: 'O₃ is a **strong oxidant**: $O_3 + 2I^- + H_2O \\to 2OH^- + I_2 + O_2$ (estimation by liberated I₂ titrated with $Na_2S_2O_3$).',
        hindi: 'O₃ एक **मजबूत ऑक्सीडेंट** है: $O_3 + 2I^- + H_2O \\to 2OH^- + I_2 + O_2$ (मुक्ति द्वारा अनुमान I₂ का शीर्षक $Na_2S_2O_3$ है)।',
            },
            {
              english: 'Atmospheric ozone (stratosphere) absorbs UV; depleted by **CFCs** (Cl radicals catalyse $O_3 \\to O_2$).',
        hindi: 'वायुमंडलीय ओजोन (समतापमंडल) यूवी को अवशोषित करता है; **सीएफसी** द्वारा समाप्त (सीएल रेडिकल्स $O_3 \\to O_2$ को उत्प्रेरित करते हैं)।',
            },
          ],
        },
      ],
    },
    {
      id: 'sulphur-allotropes',
      title: {
        english: 'Sulphur — Allotropes & H₂S',
        hindi: 'सल्फर - एलोट्रोप्स और H₂S',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Allotropes**: rhombic ($\\alpha$-S, stable < 369 K, lemon yellow), monoclinic ($\\beta$-S, stable > 369 K, needle-shaped). Both contain crown-shaped $S_8$ rings.',
        hindi: '**एलोट्रोप्स**: समचतुर्भुज ($\\alpha$-S, स्थिर <369 K, नींबू पीला), मोनोक्लिनिक ($\\beta$-S, स्थिर > 369 K, सुई के आकार का)। दोनों में मुकुट के आकार के $S_8$ छल्ले हैं।',
            },
            {
              english: 'Transition T = 369 K (96°C). Plastic / amorphous S formed on rapid quenching of molten S.',
        hindi: 'संक्रमण T = 369 K (96°C)। पिघले हुए एस के तीव्र शमन पर प्लास्टिक/अनाकार एस बनता है।',
            },
            {
              english: '$S_2$ in vapour at high T is paramagnetic (like O₂).',
        hindi: 'उच्च T पर वाष्प में $S_2$ अनुचुंबकीय (O₂ की तरह) होता है।',
            },
            {
              english: '**H₂S**: prep $FeS + H_2SO_4 \\to FeSO_4 + H_2S$. Bent, ∠HSH = 92° (almost pure p-orbitals).',
        hindi: '**H₂S**: तैयारी $FeS + H_2SO_4 \\to FeSO_4 + H_2S$। मुड़ा हुआ, ∠HSH = 92° (लगभग शुद्ध पी-ऑर्बिटल्स)।',
            },
            {
              english: 'Acidity order $H_2O < H_2S < H_2Se < H_2Te$ (bond strength $\\downarrow$).',
        hindi: 'अम्लता क्रम $H_2O < H_2S < H_2Se < H_2Te$ (बंधन शक्ति $\\downarrow$)।',
            },
            {
              english: 'Reducing power $H_2S > H_2Se > H_2Te$? Actually $H_2Te > H_2Se > H_2S$ (lower bond energy releases H more easily). Note: thermal stability $H_2O > H_2S > H_2Se > H_2Te > H_2Po$.',
        hindi: 'बिजली कम हो रही है $H_2S > H_2Se > H_2Te$? दरअसल $H_2Te > H_2Se > H_2S$ (निचली बॉन्ड ऊर्जा H को अधिक आसानी से रिलीज़ करती है)। नोट: थर्मल स्थिरता $H_2O > H_2S > H_2Se > H_2Te > H_2Po$।',
            },
          ],
        },
      ],
    },
    {
      id: 'oxides-of-sulphur',
      title: {
        english: 'Oxides & Oxoacids of Sulphur',
        hindi: 'सल्फर के ऑक्साइड और ऑक्सोअम्ल',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**SO₂**: prep — burning S, roasting sulphides ($4FeS_2 + 11O_2 \\to 2Fe_2O_3 + 8SO_2$), or $Cu + 2H_2SO_4(\\text{conc}) \\to CuSO_4 + SO_2 + 2H_2O$.',
        hindi: '**SO₂**: तैयारी - S को जलाना, सल्फाइड को भूनना ($4FeS_2 + 11O_2 \\to 2Fe_2O_3 + 8SO_2$), या $Cu + 2H_2SO_4(\\text{conc}) \\to CuSO_4 + SO_2 + 2H_2O$।',
            },
            {
              english: 'SO₂ — bent, $sp^2$, 119°, bond order 1.5, acidic anhydride of $H_2SO_3$.',
        hindi: 'SO₂ - मुड़ा हुआ, $sp^2$, 119°, बॉन्ड ऑर्डर 1.5, $H_2SO_3$ का अम्लीय एनहाइड्राइड।',
            },
            {
              english: 'Decolourises acidified $KMnO_4$ (reducing) and decolourises bromine water.',
        hindi: 'अम्लीकृत $KMnO_4$ को रंगहीन करता है (कम करता है) और ब्रोमीन पानी को रंगहीन करता है। **SO₃**: $KMnO_4$, तलीय; का एनहाइड्राइड। **$KMnO_4$** के लिए संपर्क प्रक्रिया:',
            },
            {
              english: '**SO₃**: $sp^2$, planar; anhydride of $H_2SO_4$.',
        hindi: '**SO₃**: $sp^2$, तलीय; $H_2SO_4$ का एनहाइड्राइड।',
            },
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Contact process for $H_2SO_4$**:',
        hindi: '**$H_2SO_4$** के लिए संपर्क प्रक्रिया:',
            },
            {
              english: '$S + O_2 \\to SO_2$;',
        hindi: '$S + O_2 \\to SO_2$;',
            },
            {
              english: '$2SO_2 + O_2 \\xrightleftharpoons{V_2O_5, 720\\,K} 2SO_3$;',
        hindi: '$2SO_2 + O_2 \\xrightleftharpoons{V_2O_5, 720\\,K} 2SO_3$;',
            },
            {
              english: '$SO_3 + H_2SO_4 \\to H_2S_2O_7$ (oleum); oleum + water → 2 H₂SO₄.',
        hindi: '$SO_3 + H_2SO_4 \\to H_2S_2O_7$ (ओलियम); ओलियम + पानी → 2 H₂SO₄।',
            },
            {
              english: 'Conc. $H_2SO_4$ — strong dehydrating, oxidising (dilute is only acidic). 98 %, b.p. 611 K.',
        hindi: 'सांद्र। $H_2SO_4$ - मजबूत निर्जलीकरण, ऑक्सीकरण (पतला केवल अम्लीय होता है)। 98%, बी.पी. 611 कि.',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Acid', 'Formula', 'O.S. of S', 'S–S bond?'],
          rows: [
            ['Sulphurous', '$H_2SO_3$', '+4', 'no'],
            ['Sulphuric', '$H_2SO_4$', '+6', 'no'],
            ['Pyrosulphuric (oleum)', '$H_2S_2O_7$', '+6', 'no (S–O–S)'],
            ['Thiosulphuric', '$H_2S_2O_3$', '+2 avg', 'yes'],
            ['Peroxomonosulphuric (Caro)', '$H_2SO_5$', '+6', 'no (O–O)'],
            ['Peroxodisulphuric (Marshall)', '$H_2S_2O_8$', '+6', 'no (O–O)'],
            ['Dithionic', '$H_2S_2O_6$', '+5', 'yes'],
          ],
        },
        {
          type: 'note',
          body: {
            english: 'Oleum = $H_2SO_4 + SO_3$ (in equilibrium with $H_2S_2O_7$). Dehydrates sucrose: $C_{12}H_{22}O_{11} \\xrightarrow{H_2SO_4} 12C + 11H_2O$.',
        hindi: 'ओलियम = $H_2SO_4 + SO_3$ ($H_2S_2O_7$ के साथ संतुलन में)। सुक्रोज को निर्जलित करता है: $C_{12}H_{22}O_{11} \\xrightarrow{H_2SO_4} 12C + 11H_2O$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '3O_2 \\xrightarrow{\\text{discharge}} 2O_3',
      desc: {
        english: 'Ozone formation',
        hindi: 'ओजोन का निर्माण',
      },
    },
    {
      latex: '2SO_2 + O_2 \\xrightleftharpoons{V_2O_5} 2SO_3',
      desc: {
        english: 'Contact process',
        hindi: 'संपर्क प्रक्रिया',
      },
    },
    {
      latex: '\\angle OSO = 119^\\circ,\\ \\angle HSH = 92^\\circ',
      desc: {
        english: 'Bond angles',
        hindi: 'बॉन्ड कोण',
      },
    },
    {
      latex: 'H_2O < H_2S < H_2Se < H_2Te',
      desc: {
        english: 'Acidic strength',
        hindi: 'अम्लीय शक्ति',
      },
    },
    {
      latex: 'H_2O > H_2S > H_2Se > H_2Te',
      desc: {
        english: 'Thermal stability',
        hindi: 'थर्मल स्थिरता',
      },
    },
    {
      latex: 'O_3 + 2I^- + H_2O \\to 2OH^- + I_2 + O_2',
      desc: {
        english: 'Estimation of O₃',
        hindi: 'O₃ का अनुमान',
      },
    },
    {
      latex: '4FeS_2 + 11O_2 \\to 2Fe_2O_3 + 8SO_2',
      desc: {
        english: 'Roasting pyrites',
        hindi: 'पाइराइट्स को भूनना',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Structure',
      problem: {
        english: 'Bond angle in H₂S is approximately:',
        hindi: 'H₂S में बॉन्ड कोण लगभग है:',
      },
      answer: {
        english: '92°',
        hindi: '92°',
      },
      solution: {
        english: 'Almost pure p-orbital bonding.',
        hindi: 'लगभग शुद्ध पी-ऑर्बिटल बॉन्डिंग।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Acid',
      problem: {
        english: 'Catalyst in Contact process is:',
        hindi: 'संपर्क प्रक्रिया में उत्प्रेरक है:',
      },
      answer: {
        english: 'V₂O₅',
        hindi: 'V₂O₅',
      },
      solution: {
        english: 'Replaced earlier Pt catalyst.',
        hindi: 'पहले वाले पीटी उत्प्रेरक को बदला गया।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Acidity',
      problem: {
        english: 'Correct order of acidic strength of hydrides:',
        hindi: 'हाइड्राइड्स की अम्लीय शक्ति का सही क्रम:',
      },
      answer: {
        english: '$H_2O < H_2S < H_2Se < H_2Te$',
        hindi: '$H_2O < H_2S < H_2Se < H_2Te$',
      },
      solution: {
        english: 'Bond energy $\\downarrow$ down the group.',
        hindi: 'समूह में बॉन्ड ऊर्जा $\\downarrow$।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Ozone',
      problem: {
        english: 'Bond angle in ozone molecule is:',
        hindi: 'ओजोन अणु में बंधन कोण है:',
      },
      answer: {
        english: '117°',
        hindi: '117°',
      },
      solution: {
        english: '$sp^2$ with one lone pair.',
        hindi: '$sp^2$ एक अकेली जोड़ी के साथ।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'O₂ paramagnetism',
      problem: {
        english: 'O₂ is paramagnetic because:',
        hindi: 'O₂ अनुचुंबकीय है क्योंकि:',
      },
      answer: {
        english: 'Two unpaired electrons in $\\pi^*$ MOs',
        hindi: '$\\pi^*$ MOs में दो अयुग्मित इलेक्ट्रॉन',
      },
      solution: {
        english: 'MO theory prediction.',
        hindi: 'एमओ सिद्धांत भविष्यवाणी। कॉन्फ़िगरेशन - नोबल-गैस कॉन्फ़िगरेशन से एक इलेक्ट्रॉन कम; सर्वाधिक प्रतिक्रियाशील अधातुएँ।',
      },
    },
  ],
  next: {
    slug: 'p-block-17',
    title: 'p-Block — Group 17 (Halogens)',
  },
};

export default chapter;
